import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FaBox, 
  FaTruck, 
  FaHandshake, 
  FaCheckCircle, 
  FaTimes, 
  FaClock, 
  FaExclamationTriangle, 
  FaUsers, 
  FaMapMarkerAlt,
  FaRegHandshake
} from "react-icons/fa";

const PackageStatusDisplay = ({ 
  delivery, 
  currentUser, 
  otherUser, 
  userRole, 
  otherUserRole,
  packageData 
}) => {
  const { t } = useTranslation('common');

  if (!delivery) {
    return (
      <div className="PAC-status-info">
        <span className="PAC-status-info-icon" style={{ color: 'var(--main-color)' }}>
          <FaBox />
        </span>
        <div>
          <div className="PAC-status-info-text">
            {t('package_actions.package_delivery')}
          </div>
          <div className="PAC-status-info-detail">
            {userRole === t('package_actions.sender') ? 
              t('package_actions.waiting_for_carriers') : 
              t('package_actions.available_for_delivery')
            }
          </div>
        </div>
      </div>
    );
  }

  // Get status-specific information
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'request_sent':
        return {
          icon: <FaRegHandshake />,
          color: 'var(--accent-color)',
          title: t('package_actions.delivery_request'),
          detail: userRole === t('package_actions.sender') ? 
            t('package_actions.new_delivery_request_detail') : 
            t('package_actions.request_sent_detail')
        };
      
      case 'confirmed':
        return {
          icon: <FaCheckCircle />,
          color: 'var(--accent-color)',
          title: t('package_actions.delivery_confirmed'),
          detail: t('package_actions.setting_pickup_meeting')
        };
      
      case 'pickup_meeting_agreement':
        return {
          icon: <FaClock />,
          color: '#F59E0B',
          title: t('package_actions.pickup_coordination'),
          detail: t('package_actions.agreeing_pickup_time')
        };
      
      case 'pickup_meeting_agreed':
        return {
          icon: <FaHandshake />,
          color: 'var(--accent-color)',
          title: t('package_actions.pickup_scheduled'),
          detail: delivery.pickup_meeting_time ? 
            new Date(delivery.pickup_meeting_time).toLocaleString('en-GB', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }) : 
            t('package_actions.time_confirmed')
        };
      
      case 'pickup_arrival_phase':
        return {
          icon: <FaMapMarkerAlt />,
          color: '#F59E0B',
          title: t('package_actions.pickup_time'),
          detail: t('package_actions.heading_to_pickup')
        };
      
      case 'pickup_waiting':
        return {
          icon: <FaClock />,
          color: '#F59E0B',
          title: t('package_actions.pickup_in_progress'),
          detail: t('package_actions.waiting_at_pickup')
        };
      
      case 'pickup_both_present':
        return {
          icon: <FaUsers />,
          color: 'var(--accent-color)',
          title: t('package_actions.ready_for_pickup'),
          detail: t('package_actions.both_parties_present')
        };
      
      case 'package_picked_up':
        return {
          icon: <FaTruck />,
          color: '#3B82F6',
          title: t('package_actions.in_transit'),
          detail: t('package_actions.package_with_carrier')
        };
      
      case 'delivery_meeting_agreement':
        return {
          icon: <FaClock />,
          color: '#F59E0B',
          title: t('package_actions.delivery_coordination'),
          detail: t('package_actions.arranging_delivery_time')
        };
      
      case 'delivery_meeting_agreed':
        return {
          icon: <FaTruck />,
          color: 'var(--accent-color2)',
          title: t('package_actions.delivery_countdown'),
          detail: delivery.delivery_scheduled_time ? 
            t('package_actions.delivery_scheduled_for', { 
              time: new Date(delivery.delivery_scheduled_time).toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }) 
            }) : 
            t('package_actions.delivery_time_set')
        };
      
      case 'delivery_arrival_phase':
        return {
          icon: <FaMapMarkerAlt />,
          color: '#F59E0B',
          title: t('package_actions.delivery_time'),
          detail: t('package_actions.heading_to_delivery')
        };
      
      case 'delivery_waiting':
        return {
          icon: <FaClock />,
          color: '#F59E0B',
          title: t('package_actions.delivery_in_progress'),
          detail: t('package_actions.waiting_at_delivery')
        };
      
      case 'delivery_both_present':
        return {
          icon: <FaUsers />,
          color: 'var(--accent-color)',
          title: t('package_actions.ready_for_delivery'),
          detail: t('package_actions.both_parties_present')
        };
      
      case 'package_delivered':
        return {
          icon: <FaBox />,
          color: '#8B5CF6',
          title: t('package_actions.delivered'),
          detail: t('package_actions.awaiting_confirmation')
        };
      
      case 'delivery_confirmed':
        return {
          icon: <FaCheckCircle />,
          color: 'var(--accent-color)',
          title: t('package_actions.delivery_confirmed'),
          detail: t('package_actions.receipt_confirmed')
        };
      
      case 'completed':
        return {
          icon: <FaCheckCircle />,
          color: 'var(--accent-color)',
          title: t('package_actions.delivery_completed'),
          detail: t('package_actions.all_done')
        };
      
      case 'cancelled':
        return {
          icon: <FaTimes />,
          color: 'var(--light-color)',
          title: t('package_actions.delivery_cancelled'),
          detail: t('package_actions.cancelled_by_user')
        };
      
      case 'declined':
        return {
          icon: <FaTimes />,
          color: 'var(--light-color)',
          title: t('package_actions.delivery_declined'),
          detail: t('package_actions.request_was_declined')
        };
      
      case 'pickup_sender_no_show':
        return {
          icon: <FaExclamationTriangle />,
          color: '#EF4444',
          title: t('package_actions.pickup_failed'),
          detail: t('package_actions.sender_did_not_arrive')
        };
      
      case 'pickup_carrier_no_show':
        return {
          icon: <FaExclamationTriangle />,
          color: '#EF4444',
          title: t('package_actions.pickup_failed'),
          detail: t('package_actions.carrier_did_not_arrive')
        };
      
      case 'delivery_carrier_no_show':
        return {
          icon: <FaExclamationTriangle />,
          color: '#EF4444',
          title: t('package_actions.delivery_failed'),
          detail: t('package_actions.carrier_did_not_arrive')
        };
      
      case 'delivery_recipient_no_show':
        return {
          icon: <FaExclamationTriangle />,
          color: '#EF4444',
          title: t('package_actions.delivery_failed'),
          detail: t('package_actions.recipient_did_not_arrive')
        };
      
      case 'pickup_disputed':
        return {
          icon: <FaExclamationTriangle />,
          color: '#F59E0B',
          title: t('package_actions.pickup_disputed'),
          detail: t('package_actions.presence_disputed')
        };
      
      case 'delivery_disputed':
        return {
          icon: <FaExclamationTriangle />,
          color: '#F59E0B',
          title: t('package_actions.delivery_disputed'),
          detail: t('package_actions.delivery_contested')
        };
      
      default:
        return {
          icon: <FaBox />,
          color: 'var(--main-color)',
          title: t('package_actions.package_delivery'),
          detail: t('package_actions.status_unknown')
        };
    }
  };

  const statusDisplay = getStatusDisplay(delivery.status);

  return (
    <div className="PAC-status-info">
      <span className="PAC-status-info-icon" style={{ color: statusDisplay.color }}>
        {statusDisplay.icon}
      </span>
      <div>
        <div className="PAC-status-info-text" style={{ color: statusDisplay.color }}>
          {statusDisplay.title}
        </div>
        <div className="PAC-status-info-detail">
          {statusDisplay.detail}
        </div>
        {otherUser && delivery.status === 'request_sent' && (
          <div className="PAC-status-info-detail" style={{ fontSize: '10px', opacity: 0.7 }}>
            {t('package_actions.with_username', { username: otherUser.username || t('package_actions.user') })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PackageStatusDisplay;